// Calorie and macro calculations.
//
// Mifflin–St Jeor supports only the male and female equations as
// published. Customers whose identity is "Other" or "Prefer not to say"
// must explicitly choose the equation basis or opt out of calculation.

import { getPlannerRulesSync, goalDeltaPct } from './plannerRules';

// -------- Unit conversions -------------------------------------------
export const LB_PER_KG = 2.2046226218;

export function ftInToCm(ft, inches) {
  const f = Number(ft) || 0;
  const i = Number(inches) || 0;
  return (f * 12 + i) * 2.54;
}

export function lbToKg(lb) {
  return Number(lb) / LB_PER_KG;
}

// -------- Effective basis resolution --------------------------------
// Returns 'male', 'female', or null (skip calculation).
export function resolveEffectiveBasis(sex, calculation_basis) {
  if (sex === 'male') return 'male';
  if (sex === 'female') return 'female';
  if (sex === 'other' || sex === 'prefer_not_to_say') {
    if (calculation_basis === 'male_equation') return 'male';
    if (calculation_basis === 'female_equation') return 'female';
    // 'manual' or unset → no calculation
    return null;
  }
  return null;
}

// -------- Mifflin–St Jeor -------------------------------------------
// male:   10w + 6.25h − 5a + 5
// female: 10w + 6.25h − 5a − 161
// No other branches.
export function mifflinStJeor({ basis, weight_kg, height_cm, age }) {
  const w = Number(weight_kg);
  const h = Number(height_cm);
  const a = Number(age);
  if (!(w > 0) || !(h > 0) || !(a > 0)) return null;
  const base = 10 * w + 6.25 * h - 5 * a;
  if (basis === 'male') return base + 5;
  if (basis === 'female') return base - 161;
  return null;
}

export function tdee(bmr, activity_level) {
  const rules = getPlannerRulesSync();
  const mult = rules.activity_multipliers[activity_level];
  if (!bmr || !mult) return null;
  // Match the displayed rounded BMR so the breakdown text stays internally consistent.
  return Math.round(bmr) * mult;
}

// Full estimator. Returns null when the basis is null (manual entry).
export function estimateCalories({
  basis,
  age,
  height_cm,
  weight_kg,
  activity_level,
  health_goal,
}) {
  if (!basis) return null;
  const rules = getPlannerRulesSync();
  const bmr = mifflinStJeor({ basis, weight_kg, height_cm, age });
  const total = tdee(bmr, activity_level);
  const delta_pct = goalDeltaPct(rules, health_goal);
  const target = total == null ? null : total * (1 + delta_pct / 100);
  return {
    bmr: bmr == null ? null : Math.round(bmr),
    tdee: total == null ? null : Math.round(total),
    delta_pct,
    calorie_target: target == null ? null : Math.round(target / 10) * 10,
  };
}

// -------- Macro presets ---------------------------------------------
// Automatic-mode allocation:
//   1. Protein = max(preset % of calories, preset g/kg weight floor).
//   2. Fat keeps its preset % of calories, UNLESS the protein floor
//      leaves too little remaining -- fat is only reduced (down to
//      the fat floor) once carbohydrate has already been reduced to
//      zero. This guarantees carbohydrate is never an unbounded
//      residual.
//   3. Carbohydrate absorbs whatever is left after protein + fat,
//      and is the first lever reduced when the protein floor forces
//      more calories into protein.
//   4. A final rounding-reconciliation nudges carbs so total macro
//      calories stay within normal rounding tolerance of the target.
export function deriveMacroTargets({ preset, calorie_target, weight_kg }) {
  const rules = getPlannerRulesSync();
  if (!calorie_target) return null;
  if (preset === 'custom') return null;

  const cfg = rules.macro_preset_allocations[preset] || rules.macro_preset_allocations.auto;
  const w = Number(weight_kg) || 75;

  const proteinPctKcal = (cfg.protein_pct / 100) * calorie_target;
  const proteinFloorKcal = cfg.protein_g_per_kg * w * 4;
  const proteinKcal = Math.max(proteinPctKcal, proteinFloorKcal);

  const fatFloorKcal = (rules.macro_fat_floor_pct / 100) * calorie_target;
  let fatKcal = (cfg.fat_pct / 100) * calorie_target;
  const remainingKcal = calorie_target - proteinKcal;
  let carbKcal = remainingKcal - fatKcal;

  if (carbKcal < 0) {
    // Protein floor left too little for the preset's fat target too.
    // Reduce carbs to zero first, then fat -- never below its floor.
    carbKcal = 0;
    fatKcal = Math.max(fatFloorKcal, Math.min(fatKcal, remainingKcal));
  }

  let protein_g = Math.round(proteinKcal / 4);
  let carbs_g = Math.round(Math.max(0, carbKcal) / 4);
  const fat_g = Math.round(Math.max(0, fatKcal) / 9);

  // Final reconciliation: close normal rounding gaps via carbohydrate,
  // the flexible lever, so total macro calories track calorie_target.
  const totalKcal = protein_g * 4 + carbs_g * 4 + fat_g * 9;
  const diffKcal = calorie_target - totalKcal;
  if (diffKcal !== 0) {
    carbs_g = Math.max(0, carbs_g + Math.round(diffKcal / 4));
  }

  return { protein_g, carbs_g, fat_g };
}

export function caloriesFromMacros({ protein_g, carbs_g, fat_g }) {
  const p = Number(protein_g) || 0;
  const c = Number(carbs_g) || 0;
  const f = Number(fat_g) || 0;
  return p * 4 + c * 4 + f * 9;
}

export function macroDeviationWarning({ calorie_target, protein_g, carbs_g, fat_g }) {
  const rules = getPlannerRulesSync();
  const threshold = rules.macro_deviation_warning_pct;
  if (!calorie_target) return null;
  const kcal = caloriesFromMacros({ protein_g, carbs_g, fat_g });
  if (!kcal) return null;
  const diff = kcal - calorie_target;
  const pct = (Math.abs(diff) / calorie_target) * 100;
  if (pct <= threshold) return null;
  return {
    kcal_from_macros: Math.round(kcal),
    diff: Math.round(diff),
    pct: Math.round(pct),
    direction: diff > 0 ? 'over' : 'under',
  };
}
